import {
  createLogStoreFromConfig,
  getLogStore,
  type LogStore,
  OnLogBridgeSink,
  registerLogStore,
  resetLogStore,
  resolveLogStoreConfig,
  WsLogSink,
} from "@skaile/workspaces/core/logging";
import type {
  LogEntry,
  LogEntryEvent,
  LogQuery,
  LogQueryResult,
  LogSink,
} from "@skaile/workspaces/types";

/**
 * Pre-init log sink. Active before the LogStore boots — i.e. during the
 * window between process start and `session_init` processing.
 *
 * Writes every entry to `process.stdout` (so early-boot operators / container
 * log shippers still see it). Entries are also captured in a bounded
 * in-memory ring (default cap 200 entries) so that, once the LogStore boots,
 * subscribers can read them via {@link entries} for diagnostic purposes.
 *
 * NOTE: backfilling the buffered entries into the LogStore's SQLite trail is
 * not done in v3.0 because `LogStore.record()` re-stamps id/timestamp/
 * sessionId, which would corrupt the original entries. A follow-up
 * (`AF-PV3 / F-LOG-BACKFILL`) will add a `replay()` API to LogStore so the
 * boot logs land in the persistent trail alongside the live stream.
 *
 * Spec: `_devlog/specs/2026-05-10-deterministic-session-bootstrap.md` §
 * Pre-init logging.
 */
export class PreInitRingSink implements LogSink {
  private readonly ring: LogEntry[] = [];
  private readonly cap: number;

  constructor(cap = 200) {
    this.cap = cap;
  }

  write(entry: LogEntry): void {
    process.stdout.write(formatForStdout(entry));
    this.ring.push(entry);
    if (this.ring.length > this.cap) this.ring.shift();
  }

  writeBatch(entries: LogEntry[]): void {
    for (const entry of entries) this.write(entry);
  }

  /** Optional query (no-op) — satisfies the LogSink contract. */
  query(_q: LogQuery): LogQueryResult {
    return { entries: this.ring.slice() };
  }

  /** Close hook — clears the ring so subsequent writes are dropped. */
  close(): void {
    this.ring.length = 0;
  }

  /** Number of entries currently buffered (capped at {@link cap}). */
  size(): number {
    return this.ring.length;
  }

  /** Snapshot of the currently-buffered entries. Used for diagnostics. */
  entries(): readonly LogEntry[] {
    return this.ring.slice();
  }

  /** Discard all buffered entries. Called by `bootstrapRunnerLogStore`. */
  clear(): void {
    this.ring.length = 0;
  }
}

function formatForStdout(entry: LogEntry): string {
  const ts = entry.timestamp ?? new Date().toISOString();
  const where = [entry.source?.kind, entry.source?.subkind, entry.source?.instance]
    .filter(Boolean)
    .join(":");
  const dataStr = entry.data ? ` ${JSON.stringify(entry.data)}` : "";
  return `[${ts}] [${entry.level}] [${where}] ${entry.message}${dataStr}\n`;
}

let activePreInitSink: PreInitRingSink | null = null;

/**
 * Install (or return the existing) process-wide pre-init sink. Idempotent
 * so the first import wins; later calls reuse the same ring.
 *
 * Called from the top of every runner entry point (CLI, serve.ts) before
 * any other code emits structured logs.
 */
export function installPreInitRingSink(cap = 200): PreInitRingSink {
  if (activePreInitSink) return activePreInitSink;
  activePreInitSink = new PreInitRingSink(cap);
  return activePreInitSink;
}

/** Currently-installed pre-init sink, or `null` if none has been installed. */
export function getPreInitRingSink(): PreInitRingSink | null {
  return activePreInitSink;
}

/** Drop the reference to the installed sink. Used in tests + on dispose. */
export function clearPreInitRingSink(): void {
  activePreInitSink = null;
}

/**
 * Options for {@link bootstrapRunnerLogStore}.
 *
 * @docLink packages/runner/logging#bootstrap-options
 */
export interface BootstrapOptions {
  sessionId: string;
  projectDir: string;
  /** `skaile.yaml`'s `logging:` block, if pre-loaded. */
  yamlBlock?: unknown;
  /** Back-compat callback — every entry mirrored as a formatted line. */
  onLog?: (line: string) => void;
  /**
   * Transport send callback. When provided, a `WsLogSink` is installed so
   * every recorded LogEntry flows to the connected client as a `log_entry`
   * AgentEvent. Drops silently when the transport is disconnected — that
   * gating happens inside the transport's `send()`.
   */
  transportSend?: (event: LogEntryEvent) => void;
}

/**
 * Result from {@link bootstrapRunnerLogStore}.
 *
 * @docLink packages/runner/logging#bootstrap-result
 */
export interface BootstrapResult {
  store: LogStore;
  dispose: () => void;
}

let currentSessionId: string | null = null;

/**
 * Idempotent bootstrap. If the same sessionId is re-used, returns the existing
 * registered store. If a different sessionId arrives, the previous store is
 * closed and replaced.
 * @docLink packages/runner/logging#bootstrap-runner-log-store
 */
export function bootstrapRunnerLogStore(opts: BootstrapOptions): BootstrapResult {
  const existing = getLogStore();
  if (existing && currentSessionId === opts.sessionId) {
    return { store: existing, dispose: () => disposeStore(existing) };
  }

  const cfg = resolveLogStoreConfig({
    sessionId: opts.sessionId,
    workspacePath: opts.projectDir,
    // The skaile.yaml `logging:` block is loosely typed at the workspace-config
    // layer; the resolver validates `mode` / `level` strings at runtime.
    yamlBlock: opts.yamlBlock as Parameters<typeof resolveLogStoreConfig>[0]["yamlBlock"],
  });
  const store = createLogStoreFromConfig(cfg);

  if (opts.onLog) {
    store.addSink(new OnLogBridgeSink(opts.onLog));
  }

  if (opts.transportSend) {
    store.addSink(new WsLogSink({ send: opts.transportSend }));
  }

  if (existing) disposeStore(existing);
  registerLogStore(store);
  currentSessionId = opts.sessionId;

  // Clear any entries buffered during the pre-init window (between process
  // start and session_init processing). The entries are still visible on
  // stdout (the PreInitRingSink wrote them there as they happened);
  // backfilling them into LogStore.SQLite needs a LogStore.replay()
  // upgrade tracked under follow-up `AF-PV3 / F-LOG-BACKFILL`.
  const preInit = getPreInitRingSink();
  if (preInit) preInit.clear();

  return {
    store,
    dispose: () => {
      disposeStore(store);
      currentSessionId = null;
    },
  };
}

/**
 * Reset the runner's process-singleton LogStore.
 *
 * @docLink packages/runner/logging#reset-runner-log-store
 */
export function resetRunnerLogStore(): void {
  const s = getLogStore();
  if (s) disposeStore(s);
  resetLogStore();
  currentSessionId = null;
}

function disposeStore(store: LogStore): void {
  try {
    store.flush();
    store.close();
  } catch {
    /* swallow */
  }
}
